/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    USE_REVIEW_STATUS_HOOK
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Extracted completion calculation logic into a custom hook for ReviewProfileForm.
 *
 * File Size Check:
 * - Current: ~60 lines
 * - Limit: 100 lines (hook)
 * - Decision: New hook creation (under limit)
 */
import { useMemo } from "react";
import type { User } from "../../../../interfaces/User";

const isNonEmpty = (value?: string | null) => !!value && value.trim().length > 0;

const REQUIRED_USER_FIELDS = ["firstName", "lastName", "email", "tel"] as const;
const REQUIRED_COMPANY_FIELDS = ["companyName", "country"] as const;

export const useReviewStatus = (profile: Partial<User> | null) => {
  return useMemo(() => {
    if (!profile) {
      return { completed: 0, total: 3, percentage: 0 };
    }

    // Company completion: only name and country are required
    const companyComplete = REQUIRED_COMPANY_FIELDS.every((field) => {
      const value = profile[field as keyof User];
      return isNonEmpty(typeof value === "string" ? value : undefined);
    });

    // User profile completion: firstName, lastName, email, and phone (tel) are required
    const profileComplete = REQUIRED_USER_FIELDS.every((field) => {
      const value = profile[field as keyof User];
      return isNonEmpty(typeof value === "string" ? value : undefined);
    });

    // Alerts section placeholder
    const alertsComplete = true;

    const completed = [companyComplete, profileComplete, alertsComplete].filter(Boolean).length;

    return {
      completed,
      total: 3,
      percentage: Math.round((completed / 3) * 100),
    };
  }, [profile]);
};
